import { useEffect, useId, useLayoutEffect, useRef, useState, type FC, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../ui/button/Button';
import { Label } from './Label';
import { INPUT_BASE, INPUT_PADDING, INPUT_STATE } from './input/inputStyles';
import { floatingMenuPosition, type FloatingMenuPosition } from '@/shared/utils/floatingMenuPosition';

export interface MultiSelectOption {
    id: string;
    name: string;
    disabled?: boolean;
}

interface MultiSelectProps {
    label: string;
    options: MultiSelectOption[];
    value: string[];
    onChange: (next: string[]) => void;
    required?: boolean;
    error?: string;
    loading?: boolean;
    disabled?: boolean;
    selectAllLabel?: string;
    deselectAllLabel?: string;
    className?: string;
}

const PLACEHOLDER = 'Select options';
// Proxy for "does the joined label list fit in the closed field": there is no
// layout pass available at render time, so a character-count threshold stands
// in for measuring actual pixel width against the field's rendered size.
const SUMMARY_CHAR_LIMIT = 40;
const PANEL_MAX_HEIGHT = 280;

// Controlled multi-select dropdown: a closed-by-default field that opens a portalled,
// floating listbox panel. Rows are plain text with a tick mark for the selected state
// (no checkboxes/radios) — the aria-multiselectable listbox pattern carries the
// accessibility contract instead. No form-library coupling; consumers wrap it in a Controller.
export const MultiSelect: FC<MultiSelectProps> = ({
    label,
    options,
    value,
    onChange,
    required = false,
    error,
    loading = false,
    disabled = false,
    selectAllLabel = 'All',
    deselectAllLabel = 'Deselect All',
    className,
}) => {
    const errorId = useId();
    const labelId = useId();
    const rid = useId();
    const listboxId = `${rid}-listbox`;
    const optionId = (idx: number) => `${rid}-opt-${idx}`;

    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const [panelPos, setPanelPos] = useState<FloatingMenuPosition | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const inert = loading || disabled;
    // The panel is unmounted while inert, so the disclosure attributes must not keep
    // announcing an expanded control that points at a listbox no longer in the document.
    const expanded = open && !inert;

    const selectableOptions = options.filter((o) => !o.disabled);
    const allSelected = selectableOptions.length > 0 && selectableOptions.every((o) => value.includes(o.id));
    const toggleAllLabel = allSelected ? deselectAllLabel : selectAllLabel;
    const selectedOptions = options.filter((o) => value.includes(o.id));
    // Clamp into [0, len-1] so a shrunk option list never leaves the highlight
    // (or aria-activedescendant / Enter) pointing out of range.
    const activeIndex = options.length ? Math.min(Math.max(highlighted, 0), options.length - 1) : 0;

    const toggleOption = (id: string) => {
        if (options.find((o) => o.id === id)?.disabled) return;
        onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
    };

    // Disabled rows cannot be toggled individually, so a bulk action must never strand
    // an already-selected disabled id the consumer would then be unable to restore.
    const toggleAll = () => {
        const selectedDisabledIds = value.filter((id) => options.find((o) => o.id === id)?.disabled);
        onChange(allSelected ? selectedDisabledIds : [...selectedDisabledIds, ...selectableOptions.map((o) => o.id)]);
    };

    const openPanel = () => {
        setHighlighted(0);
        setOpen(true);
    };

    const closePanel = (refocus: boolean) => {
        setOpen(false);
        if (refocus) triggerRef.current?.focus();
    };

    // A panel left open while the field becomes inert would reappear once it is interactive again.
    useEffect(() => {
        if (inert) setOpen(false);
    }, [inert]);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        setPanelPos(floatingMenuPosition(triggerRef.current.getBoundingClientRect(), window.innerHeight, PANEL_MAX_HEIGHT));
    }, [open]);

    // Close on a click outside both the trigger and the portalled panel.
    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // A fixed-position panel can't follow scroll/resize, so close it to avoid drift.
    useEffect(() => {
        if (!open) return;
        const handleScroll = (event: Event) => {
            if (panelRef.current?.contains(event.target as Node)) return;
            setOpen(false);
        };
        const handleResize = () => setOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [open]);

    const handleTriggerClick = () => {
        if (inert) return;
        if (open) {
            closePanel(false);
        } else {
            openPanel();
        }
    };

    const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (inert) return;
        if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                openPanel();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted(Math.min(activeIndex + 1, Math.max(options.length - 1, 0)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(Math.max(activeIndex - 1, 0));
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const opt = options[activeIndex];
            if (opt) toggleOption(opt.id);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closePanel(true);
        } else if (e.key === 'Tab') {
            // No preventDefault: let focus continue to the next tabbable element.
            setOpen(false);
        }
    };

    const joinedNames = selectedOptions.map((o) => o.name).join(', ');
    const summary = loading
        ? 'Loading...'
        : selectedOptions.length === 0
            ? PLACEHOLDER
            : allSelected && selectableOptions.length === options.length
                ? 'All'
                : joinedNames.length <= SUMMARY_CHAR_LIMIT
                    ? joinedNames
                    : `${selectedOptions.length} selected`;

    return (
        <div className={className}>
            <Label id={labelId} required={required}>{label}</Label>

            <button
                ref={triggerRef}
                type="button"
                className={twMerge(
                    INPUT_BASE,
                    INPUT_PADDING,
                    'relative flex items-center justify-between transition-colors',
                    inert
                        ? INPUT_STATE.disabled
                        : `cursor-pointer ${error ? INPUT_STATE.error : INPUT_STATE.default}`
                )}
                disabled={inert}
                aria-haspopup="listbox"
                aria-expanded={expanded}
                aria-labelledby={labelId}
                aria-controls={expanded ? listboxId : undefined}
                aria-activedescendant={expanded && options.length ? optionId(activeIndex) : undefined}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
            >
                <span className={`mr-5 truncate text-left ${selectedOptions.length ? 'text-mp-foreground' : 'text-mp-muted'}`}>
                    {summary}
                </span>
                <ChevronDownIcon
                    className={`shrink-0 text-mp-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                    size={20}
                />
            </button>

            {expanded && panelPos && createPortal(
                <div
                    ref={panelRef}
                    style={{
                        position: 'fixed',
                        top: panelPos.top,
                        bottom: panelPos.bottom,
                        left: panelPos.left,
                        width: panelPos.width,
                        maxHeight: panelPos.maxHeight,
                        zIndex: 100000,
                    }}
                    className="flex flex-col rounded-lg border border-mp-border bg-mp-surface shadow-lg"
                >
                    <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        onClick={toggleAll}
                        ariaPressed={allSelected}
                        ariaLabel={`${toggleAllLabel} ${label} options`}
                        disabled={selectableOptions.length === 0}
                        className="w-full shrink-0 justify-start rounded-none rounded-t-lg border-x-0 border-t-0"
                    >
                        {toggleAllLabel}
                    </Button>

                    <ul
                        id={listboxId}
                        role="listbox"
                        aria-multiselectable="true"
                        className="max-h-60 min-h-0 overflow-y-auto py-1"
                    >
                        {options.length === 0 && (
                            <li className="px-3 py-2 text-sm text-mp-muted">No options available.</li>
                        )}
                        {options.map((option, idx) => {
                            const selected = value.includes(option.id);
                            const active = idx === activeIndex;
                            return (
                                <li
                                    key={option.id}
                                    id={optionId(idx)}
                                    role="option"
                                    aria-selected={selected}
                                    aria-disabled={option.disabled || undefined}
                                    className={`flex items-center justify-between gap-2 px-3 py-2 text-sm ${
                                        option.disabled
                                            ? 'cursor-not-allowed opacity-50'
                                            : `cursor-pointer ${
                                                selected
                                                    ? 'bg-mp-primary-content text-mp-foreground'
                                                    : active
                                                        ? 'bg-mp-background'
                                                        : 'hover:bg-mp-background'
                                            }`
                                    }`}
                                    onClick={() => !option.disabled && toggleOption(option.id)}
                                    onMouseEnter={() => setHighlighted(idx)}
                                >
                                    <span>{option.name}</span>
                                    {selected && <CheckIcon size={16} className="shrink-0" />}
                                </li>
                            );
                        })}
                    </ul>
                </div>,
                document.body,
            )}

            <div className="min-h-5">
                {error && <p id={errorId} className="text-sm text-mp-error">{error}</p>}
            </div>
        </div>
    );
};
